import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchDashboardData,
  type DashboardData,
  type DashboardLatestItem,
  type DashboardSignal,
  type DashboardPoint,
} from "../../api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Legend,
  Tooltip,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import type { ChartOptions } from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, Legend, Tooltip);

// ══════════════════════════════════════════════════════════════════════
// Helpers
// ══════════════════════════════════════════════════════════════════════
function fmtDelta(d: number | null | undefined, prev?: string | null): string {
  if (d === null || d === undefined) return "";
  if (d === 0) return `→ 持平${prev ? ` vs ${prev}` : ""}`;
  const arrow = d > 0 ? "↑" : "↓";
  return `${arrow}${Math.abs(d).toFixed(1)}${prev ? ` vs ${prev}` : ""}`;
}

function lastV(arr?: DashboardPoint[]): number | null {
  return arr?.length ? arr[arr.length - 1].v : null;
}

function lat(latest: Record<string, DashboardLatestItem>, key: string) {
  return latest[key] || null;
}

// Dynamic narrative builder — picks what to highlight based on actual values
// (used inline below, not as standalone function)

// Chart shared config — must resolve CSS variables at render time for dark mode
function getCSSVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function chartColors() {
  return {
    grid: getCSSVar("--chart-grid") || "rgba(58,41,19,0.08)",
    axis: getCSSVar("--chart-axis") || "#8A7F72",
    tooltipBg: getCSSVar("--chart-tooltip-bg") || "#FFFDF8",
    tooltipBorder: getCSSVar("--chart-tooltip-border") || "rgba(58,41,19,0.12)",
    ink: getCSSVar("--color-ink") || "#181512",
    inkSec: getCSSVar("--color-ink-secondary") || "#4E4942",
  };
}

function makeChartTooltip() {
  const c = chartColors();
  return { backgroundColor: c.tooltipBg, borderColor: c.tooltipBorder, borderWidth: 1, titleColor: c.ink, bodyColor: c.inkSec, titleFont: { size: 11 }, bodyFont: { size: 11 }, padding: 10, cornerRadius: 6 };
}

function makeChartLegend() {
  const c = chartColors();
  return { position: "top" as const, labels: { font: { size: 11 }, boxWidth: 12, padding: 8, color: c.inkSec } };
}

// ══════════════════════════════════════════════════════════════════════
// Sub-components — all use CSS variable colors for dark mode (CR fix)
// ══════════════════════════════════════════════════════════════════════

function GaugeBar({ score, label }: { score: number; label: string }) {
  const pct = Math.max(5, score);
  const endColors: Record<string, string> = {
    "极冷": "#3B82F6", "偏冷": "#0D9488", "中性": "#B8860B", "偏热": "#C9A96E", "过热": "#C4362C",
  };
  return (
    <div className="paper-panel border-subtle p-5">
      <div className="text-label mb-3">综合水温指数</div>
      <div className="relative h-8 bg-surface-sunken rounded-full overflow-hidden mb-2">
        <div
          className="h-full rounded-full flex items-center justify-end pr-3 transition-all duration-700"
          style={{ width: `${pct}%`, background: `linear-gradient(90deg, #3B82F6, ${endColors[label] || "#0D9488"})` }}
        >
          <span className="text-xs font-bold text-white drop-shadow-sm tabular-nums">{label} {Math.round(score)}</span>
        </div>
      </div>
      <div className="flex justify-between text-2xs text-ink-faint px-0.5">
        <span>🥶 极冷 0</span><span>❄️ 偏冷 25</span><span>🌤️ 中性 50</span><span>🔥 偏热 75</span><span>🌋 过热 100</span>
      </div>
    </div>
  );
}

function DriverCard({ type, title, value, unit, delta, note }: {
  type: "supply" | "demand" | "risk"; title: string; value: string; unit?: string; delta: string; note: string;
}) {
  // Use semantic colors that work in both light and dark
  const topColor = { supply: "bg-info dark:bg-info-dark", demand: "bg-warning dark:bg-warning-dark", risk: "bg-negative dark:bg-negative-dark" }[type];
  const tagColor = { supply: "text-info dark:text-info-dark", demand: "text-warning dark:text-warning-dark", risk: "text-negative dark:text-negative-dark" }[type];
  const tagLabel = { supply: "供给端", demand: "需求端", risk: "风险端" }[type];
  return (
    <div className="flex-1 paper-panel border-subtle p-4 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${topColor}`} />
      <div className={`text-2xs font-semibold uppercase tracking-wide mb-1.5 ${tagColor}`}>{tagLabel}</div>
      <div className="text-sm font-semibold text-ink mb-1">{title}</div>
      <div className="text-2xl font-bold text-ink tabular-nums tracking-tight leading-none mb-1">
        {value}{unit && <span className="text-sm font-normal text-ink-tertiary">{unit}</span>}
      </div>
      <div className="text-2xs font-medium text-ink-secondary">{delta}</div>
      <div className="text-2xs text-ink-faint mt-1">{note}</div>
    </div>
  );
}

function MetricCard({ label, value, unit, detail, accent }: {
  label: string; value: string; unit?: string; detail: string;
  accent?: "blue" | "red" | "amber" | "teal" | "green";
}) {
  const borderCls = {
    blue: "border-l-info dark:border-l-info-dark", red: "border-l-negative dark:border-l-negative-dark", amber: "border-l-warning dark:border-l-warning-dark",
    teal: "border-l-positive dark:border-l-positive-dark", green: "border-l-positive dark:border-l-positive-dark",
  }[accent || "blue"];
  return (
    <div className={`flex-1 paper-panel border-subtle p-3.5 border-l-[3px] ${borderCls}`}>
      <div className="text-2xs font-medium text-ink-tertiary mb-1">{label}</div>
      <div className="text-xl font-bold text-ink tabular-nums tracking-tight leading-none">
        {value}{unit && <span className="text-xs font-normal text-ink-tertiary">{unit}</span>}
      </div>
      <div className="text-2xs text-ink-tertiary mt-1 leading-snug">{detail}</div>
    </div>
  );
}

// ── Signal module config ────────────────────────────────────────────
const SIGNAL_MODULE: Record<string, { label: string; accent: string }> = {
  data_insight:    { label: "数据变化",   accent: "border-l-info dark:border-l-info-dark" },
  article:         { label: "政策与市场", accent: "border-l-ink-tertiary" },
  industry_event:  { label: "行业动向",   accent: "border-l-negative dark:border-l-negative-dark" },
  sentiment_spike: { label: "舆情异动",   accent: "border-l-negative/60 dark:border-l-negative-dark/60" },
  sentiment_event: { label: "舆情事件",   accent: "border-l-ink-faint" },
};

// ── Fenfu mini card (compact, full-width) ──────────────────────────
function FenfuCard({ signal }: { signal: DashboardSignal }) {
  const total = signal.total || 0;
  const negPct = signal.neg_pct || 0;
  const posPct = total > 0 ? Math.round(((signal.positive || 0) / total) * 100) : 0;
  const neuPct = Math.max(0, 100 - negPct - posPct);

  return (
    <div className="paper-panel border-subtle px-4 py-3 flex gap-4 items-center">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-2xs font-semibold text-warning dark:text-warning-dark">分付舆情</span>
          <span className="text-xs font-semibold text-ink">{signal.title?.replace("分付舆情：", "") || "—"}</span>
          <span className="text-2xs text-ink-faint tabular-nums">{total} 条</span>
        </div>
        {signal.summary && (
          <p className="text-2xs text-ink-tertiary mt-0.5 leading-relaxed line-clamp-1">{signal.summary}</p>
        )}
      </div>
      {/* Compact sentiment bar */}
      <div className="shrink-0 w-32">
        <div className="h-1.5 rounded-full overflow-hidden flex bg-surface-sunken">
          {negPct > 0 && <div className="bg-negative dark:bg-negative-dark" style={{ width: `${negPct}%` }} />}
          {neuPct > 0 && <div className="bg-ink-faint/30" style={{ width: `${neuPct}%` }} />}
          {posPct > 0 && <div className="bg-positive dark:bg-positive-dark" style={{ width: `${posPct}%` }} />}
        </div>
        <div className="flex justify-between mt-0.5 text-2xs tabular-nums">
          <span className="text-negative dark:text-negative-dark">{negPct}%</span>
          <span className="text-positive dark:text-positive-dark">{posPct}%</span>
        </div>
      </div>
    </div>
  );
}

// ── Metric labels for drill-down drawer ────────────────────────────
const METRIC_LABELS: Record<string, { label: string; unit?: string }> = {
  loan: { label: "贷款余额增速", unit: "%" },
  enterprise_rate: { label: "企业贷款利率", unit: "%" },
  housing_rate: { label: "住房贷款利率", unit: "%" },
  shibor_ib: { label: "同业拆借利率", unit: "%" },
  m2: { label: "M2 同比增速", unit: "%" },
  m1: { label: "M1 同比增速", unit: "%" },
  monetary_feel: { label: "货币政策感受" },
  banking_profit: { label: "银行盈利指数" },
  loan_demand: { label: "贷款需求指数" },
  employment: { label: "就业感受指数" },
  income_feel: { label: "收入感受指数" },
  consumption: { label: "消费意愿指数" },
  hh_short_consumer_yoy: { label: "短期消费贷增速", unit: "%" },
  hh_short_biz_yoy: { label: "短期经营贷增速", unit: "%" },
  hh_long_consumer_yoy: { label: "中长期消费贷增速", unit: "%" },
  hh_long_biz_yoy: { label: "中长期经营贷增速", unit: "%" },
  pmi_cons: { label: "建筑业 PMI" },
  pmi_order: { label: "服务业新订单" },
  pmi_nfg: { label: "非制造业 PMI" },
  pmi_svc: { label: "服务业 PMI" },
  re_inv: { label: "房地产开发投资完成额", unit: "亿元" },
  ridehail: { label: "网约车月订单", unit: "亿" },
  urate_nation: { label: "城镇调查失业率", unit: "%" },
  urate_31cities: { label: "31大城市失业率", unit: "%" },
  urate_youth: { label: "16-24岁青年失业率", unit: "%" },
};

// ── Signal drill-down drawer (inline panel) ────────────────────────
function SignalDrawer({
  signal,
  series,
  industry,
  onClose,
}: {
  signal: DashboardSignal;
  series: Record<string, DashboardPoint[]>;
  industry: Record<string, DashboardPoint[]>;
  onClose: () => void;
}) {
  // Drill-down content varies by signal type
  const renderBody = () => {
    if (signal.type === "data_insight" && signal.context_key) {
      const key = signal.context_key;
      const pts = series?.[key] || industry?.[key] || [];
      const meta = METRIC_LABELS[key] || { label: key };
      if (pts.length === 0) {
        return <p className="text-xs text-ink-tertiary">暂无历史序列数据。</p>;
      }
      const labels = mapToLabels(pts);
      const values = mapToValues(pts);
      return (
        <div className="space-y-3">
          {signal.summary && (
            <p className="text-xs text-ink-secondary leading-relaxed">{signal.summary}</p>
          )}
          <div className="paper-panel border-subtle p-3">
            <div className="text-2xs font-medium text-ink-tertiary mb-2">
              {meta.label}（近 {pts.length} 期）
            </div>
            <Line
              data={{
                labels,
                datasets: [
                  {
                    label: meta.label,
                    data: values,
                    borderColor: "#3B82F6",
                    backgroundColor: "rgba(59,130,246,0.08)",
                    borderWidth: 2,
                    pointRadius: 2,
                    tension: 0.3,
                    fill: true,
                  },
                ],
              }}
              options={lineOpts(
                2.4,
                undefined,
                undefined,
                meta.unit === "%" ? (v) => Number(v) + "%" : undefined,
              )}
            />
          </div>
          <div className="text-2xs text-ink-tertiary">
            最近值：
            <span className="font-semibold text-ink tabular-nums">
              {values[values.length - 1]}
              {meta.unit || ""}
            </span>
            <span className="ml-1">（{labels[labels.length - 1]}）</span>
          </div>
        </div>
      );
    }

    if (signal.type === "fenfu_sentiment") {
      const total = signal.total || 0;
      const neg = signal.negative || 0;
      const pos = signal.positive || 0;
      const negPct = signal.neg_pct || 0;
      const posPct = total > 0 ? Math.round((pos / total) * 100) : 0;
      const neuPct = Math.max(0, 100 - negPct - posPct);
      return (
        <div className="space-y-3">
          {signal.summary && (
            <p className="text-xs text-ink-secondary leading-relaxed">{signal.summary}</p>
          )}
          <div className="paper-panel border-subtle p-3">
            <div className="text-2xs font-medium text-ink-tertiary mb-2">近 30 天情感分布</div>
            <div className="h-3 rounded-full overflow-hidden flex bg-surface-sunken mb-2">
              {negPct > 0 && (
                <div className="bg-negative dark:bg-negative-dark" style={{ width: `${negPct}%` }} />
              )}
              {neuPct > 0 && <div className="bg-ink-faint/30" style={{ width: `${neuPct}%` }} />}
              {posPct > 0 && (
                <div className="bg-positive dark:bg-positive-dark" style={{ width: `${posPct}%` }} />
              )}
            </div>
            <div className="flex justify-between text-2xs tabular-nums">
              <span className="text-negative dark:text-negative-dark">
                负面 {neg}（{negPct}%）
              </span>
              <span className="text-ink-tertiary">总计 {total}</span>
              <span className="text-positive dark:text-positive-dark">
                正面 {pos}（{posPct}%）
              </span>
            </div>
          </div>
          <Link
            to="/sentiment?product=%E5%88%86%E4%BB%98"
            className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
            onClick={onClose}
          >
            查看分付完整舆情分析 →
          </Link>
        </div>
      );
    }

    if (signal.type === "industry_event") {
      const affected = signal.affected_products || [];
      return (
        <div className="space-y-3">
          {signal.summary && (
            <p className="text-xs text-ink-secondary leading-relaxed">{signal.summary}</p>
          )}
          {affected.length > 0 && (
            <div className="paper-panel border-subtle p-3">
              <div className="text-2xs font-medium text-ink-tertiary mb-2">涉及产品</div>
              <div className="flex flex-wrap gap-1.5">
                {affected.map((p) => (
                  <span
                    key={p}
                    className="text-2xs px-2 py-0.5 rounded bg-surface-sunken text-ink-secondary border border-border"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}
          {signal.issue_type && (
            <Link
              to={`/industry?issue=${encodeURIComponent(signal.issue_type)}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
              onClick={onClose}
            >
              查看行业动向详情 →
            </Link>
          )}
          {signal.product && !signal.issue_type && (
            <Link
              to={`/industry?product=${encodeURIComponent(signal.product)}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
              onClick={onClose}
            >
              查看 {signal.product} 产品详情 →
            </Link>
          )}
        </div>
      );
    }

    // sentiment_spike / sentiment_event fallback
    return (
      <div className="space-y-3">
        {signal.summary && (
          <p className="text-xs text-ink-secondary leading-relaxed">{signal.summary}</p>
        )}
        <Link
          to="/sentiment"
          className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
          onClick={onClose}
        >
          查看舆情中心 →
        </Link>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative paper-panel border-subtle w-full sm:max-w-xl max-h-[80vh] overflow-y-auto rounded-t-xl sm:rounded-xl p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-2xs font-semibold text-ink-tertiary uppercase tracking-wide mb-1">
              {SIGNAL_MODULE[signal.type]?.label || "信号详情"}
            </div>
            <div className="text-sm font-semibold text-ink leading-snug">
              {signal.title || "—"}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="关闭"
            className="shrink-0 w-6 h-6 rounded inline-flex items-center justify-center text-ink-tertiary hover:text-ink hover:bg-surface-sunken"
          >
            ✕
          </button>
        </div>
        {renderBody()}
      </div>
    </div>
  );
}

// ── Signal section: fenfu card + modular columns ───────────────────
function SignalSection({
  signals,
  generatedAt,
  series,
  industry,
}: {
  signals: DashboardSignal[];
  generatedAt?: string;
  series: Record<string, DashboardPoint[]>;
  industry: Record<string, DashboardPoint[]>;
}) {
  const [expandedSignal, setExpandedSignal] = useState<DashboardSignal | null>(null);
  if (!signals.length) return null;

  const fenfu = signals.find((s) => s.type === "fenfu_sentiment");
  const rest = signals.filter((s) => s.type !== "fenfu_sentiment");

  // Group rest by type, preserve backend score order
  const modules: Array<{ type: string; label: string; accent: string; items: DashboardSignal[] }> = [];
  const seen = new Set<string>();
  for (const s of rest) {
    if (!seen.has(s.type)) {
      seen.add(s.type);
      const cfg = SIGNAL_MODULE[s.type] || { label: s.type, accent: "border-l-border" };
      modules.push({ type: s.type, ...cfg, items: [] });
    }
    modules.find((m) => m.type === s.type)!.items.push(s);
  }

  // Renders a single signal row — article routes to detail page, others open drawer.
  // Exposed as a named component (not inline) so React keyboard/accessibility props apply.
  const SignalItem = ({ s, i }: { s: DashboardSignal; i: number }) => {
    const titleCls = `text-xs leading-snug ${i === 0 ? "font-medium text-ink" : "text-ink-secondary"}`;
    const body = (
      <>
        <div className={`${titleCls} group-hover:text-accent transition-colors`}>
          {s.title || s.product || "—"}
        </div>
        {i === 0 && s.summary && (
          <div className="text-2xs text-ink-tertiary mt-0.5 leading-relaxed line-clamp-2">
            {s.summary}
          </div>
        )}
      </>
    );
    if (s.type === "article" && s.id) {
      return (
        <Link
          to={`/article/${s.id}`}
          className="group block cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded -mx-1 px-1"
        >
          {body}
        </Link>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setExpandedSignal(s)}
        className="group block w-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded -mx-1 px-1"
      >
        {body}
      </button>
    );
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-baseline gap-2 px-0.5">
        <span className="text-sm font-semibold text-ink">近期信号</span>
        <span className="text-2xs text-ink-faint">{signals.length} 条</span>
        {generatedAt && (
          <span className="text-2xs text-ink-faint ml-auto tabular-nums">
            {new Date(generatedAt).toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </div>

      {/* Fenfu feature card — clickable to open drawer */}
      {fenfu && (
        <button
          type="button"
          onClick={() => setExpandedSignal(fenfu)}
          className="block w-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-lg transition-shadow hover:shadow-sm"
        >
          <FenfuCard signal={fenfu} />
        </button>
      )}

      {/* Module columns — auto-fit, each module is a compact card */}
      {modules.length > 0 && (
        <div className={`grid gap-2.5 ${
          modules.length === 1 ? "grid-cols-1" :
          modules.length === 2 ? "grid-cols-2" :
          modules.length >= 3 ? "grid-cols-3" : ""
        }`}>
          {modules.map((mod) => (
            <div key={mod.type} className={`paper-panel border-subtle border-l-[2px] ${mod.accent} px-3 py-2.5`}>
              <div className="text-2xs font-semibold text-ink-tertiary mb-1.5">{mod.label}</div>
              <div className="space-y-1.5">
                {mod.items.slice(0, 3).map((s, i) => (
                  <SignalItem key={i} s={s} i={i} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Inline drill-down drawer */}
      {expandedSignal && (
        <SignalDrawer
          signal={expandedSignal}
          series={series}
          industry={industry}
          onClose={() => setExpandedSignal(null)}
        />
      )}
    </div>
  );
}

function SectionHeader({ num, title, consequence }: { num: number; title: string; consequence?: string }) {
  return (
    <div className="flex items-baseline gap-2.5 mb-3 pb-2 border-b border-border">
      <span className="w-5 h-5 bg-primary-700 dark:bg-accent text-white dark:text-primary-900 text-2xs font-bold rounded-full inline-flex items-center justify-center shrink-0">{num}</span>
      <span className="text-sm font-semibold text-ink">{title}</span>
      {consequence && <span className="text-2xs text-ink-tertiary">→ {consequence}</span>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Charts — using react-chartjs-2 with CSS variable colors
// ══════════════════════════════════════════════════════════════════════
function mapToLabels(arr: DashboardPoint[]) { return arr.map((d) => d.p); }
function mapToValues(arr: DashboardPoint[]) { return arr.map((d) => d.v); }
function alignToLabels(labels: string[], arr: DashboardPoint[]) {
  const m = Object.fromEntries(arr.map((d) => [d.p, d.v]));
  return labels.map((l) => m[l] ?? null);
}

const lineOpts = (aspectRatio = 2, yMin?: number, yMax?: number, yCallback?: (v: string | number) => string): ChartOptions<"line"> => {
  const c = chartColors();
  const grid = { color: c.grid };
  const tick = { color: c.axis, font: { size: 10 } };
  return {
    responsive: true, maintainAspectRatio: true, aspectRatio,
    interaction: { mode: "index" as const, intersect: false },
    plugins: { legend: makeChartLegend(), tooltip: makeChartTooltip() },
    scales: {
      x: { grid, ticks: { ...tick, maxRotation: 45, autoSkip: true, maxTicksLimit: 12 } },
      y: { grid, ticks: { ...tick, ...(yCallback ? { callback: yCallback } : {}) }, ...(yMin !== undefined ? { suggestedMin: yMin } : {}), ...(yMax !== undefined ? { suggestedMax: yMax } : {}) },
    },
  };
};

// ══════════════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════════════
export default function DashboardPage() {
  const { data, isLoading, isError } = useQuery<DashboardData>({
    queryKey: ["dashboard"],
    queryFn: fetchDashboardData,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 10,
  });

  if (isLoading) return (
    <div className="flex items-center justify-center py-24">
      <div className="flex items-center gap-3 text-sm text-ink-tertiary">
        <div className="w-4 h-4 border-2 border-border border-t-accent rounded-full animate-spin" />
        加载水温数据...
      </div>
    </div>
  );

  if (isError || !data) return (
    <div className="text-center py-24">
      <p className="text-lg font-semibold text-ink-secondary mb-2">数据加载失败</p>
      <p className="text-sm text-ink-tertiary">请检查后端服务是否启动</p>
    </div>
  );

  const { latest, temperature: temp, signals, series, industry, microloan } = data;
  const ent = lat(latest, "enterprise_rate");
  const house = lat(latest, "housing_rate");
  const demand = lat(latest, "loan_demand");
  const emp = lat(latest, "employment");
  const m2 = lat(latest, "m2");
  const m1 = lat(latest, "m1");
  const loan = lat(latest, "loan");
  const mpf = lat(latest, "monetary_feel");
  const profit = lat(latest, "banking_profit");
  const cons = lat(latest, "pmi_cons");
  const order = lat(latest, "pmi_order");
  const nfg = lat(latest, "pmi_nfg");
  const ride = lat(latest, "ridehail");
  // Credit structure — household loan YoY growth
  const hhShortConsYoy = lat(latest, "hh_short_consumer_yoy");
  const hhShortBizYoy = lat(latest, "hh_short_biz_yoy");
  // Funding cost
  const shiborIb = lat(latest, "shibor_ib");
  // Macro employment — objective leading indicators for post-loan risk
  const urateNation = lat(latest, "urate_nation");
  const urate31Cities = lat(latest, "urate_31cities");
  const urateYouth = lat(latest, "urate_youth");
  const mlInstArr = microloan?.ml_inst || [];
  const mlBalArr = microloan?.ml_bal || [];
  const mlInstV = lastV(mlInstArr);
  const mlInstP = mlInstArr.length ? mlInstArr[mlInstArr.length - 1].p : "";

  // Dynamic hero — picks the most notable signals
  const labelMap: Record<string, string> = { "极冷": "极冷", "偏冷": "偏冷", "中性": "温和", "偏热": "偏热", "过热": "过热" };
  const heroVerdict = `信贷行业${labelMap[temp.label] || temp.label}：` +
    (ent ? `利率${ent.value < 3.5 ? "探底" : "走低"}` : "") +
    (demand ? `，需求${demand.value < 60 ? "偏弱" : "回暖"}` : "") +
    (emp ? `，就业${emp.value < 35 ? "承压" : "企稳"}` : "");

  // Chart data
  const rateLabels = mapToLabels(series?.enterprise_rate || []);
  const demandLabels = mapToLabels(series?.loan_demand || []);
  const pmiLabels = mapToLabels(industry?.pmi_nfg || []);
  const reInvData = industry?.re_inv || [];
  const empLabels = mapToLabels(series?.employment || []);
  const mlLabels = mapToLabels(mlInstArr);

  return (
    <div className="space-y-5 pb-8">
      {/* ── Hero ── */}
      <div className="paper-panel border-subtle p-6">
        <h1 className="text-xl font-bold text-ink tracking-tight leading-snug mb-2">{heroVerdict}</h1>
        <p className="text-sm text-ink-secondary leading-relaxed max-w-3xl">
          {ent && <>企业贷款利率 <strong className="text-ink">{ent.value}%</strong>（{ent.period}）</>}
          {m2 && <>，M2 增速 <strong className="text-ink">{m2.value}%</strong></>}
          {loan && <>，贷款余额增速 <strong className="text-ink">{loan.value}%</strong></>}
          {demand && <>，贷款需求指数 <strong className="text-ink">{demand.value}</strong></>}
          {emp && <>，就业感受指数 <strong className="text-ink">{emp.value}</strong></>}。
        </p>
      </div>

      {/* ── Signals ── */}
      <SignalSection
        signals={signals || []}
        generatedAt={data.generated_at}
        series={series || {}}
        industry={industry || {}}
      />

      {/* Social media freshness alert */}
      {data.social_freshness?.stale && (
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-warning/5 dark:bg-warning-dark/5 border border-warning/20 dark:border-warning-dark/20">
          <span className="text-sm shrink-0 mt-0.5">⚠️</span>
          <div>
            <div className="text-xs font-semibold text-ink">社媒舆情数据可能过期</div>
            <div className="text-2xs text-ink-tertiary mt-0.5">
              最近一次抓取：{data.social_freshness.latest ? new Date(data.social_freshness.latest).toLocaleDateString("zh-CN") : "无数据"}
              {data.social_freshness.last_3d_count > 0
                ? ` · 近3天 ${data.social_freshness.last_3d_count} 条（${data.social_freshness.last_3d_products} 个产品）`
                : " · 近3天无新数据"
              }
              。请检查小红书 CDP 登录态是否有效，手动执行 <code className="text-2xs bg-surface-sunken px-1 py-0.5 rounded">scripts/xhs_cdp_crawl.py --status</code> 查看状态。
            </div>
          </div>
        </div>
      )}

      {/* ── Gauge + Drivers ── */}
      <div className="flex gap-4 items-stretch">
        <div className="flex-[2]"><GaugeBar score={temp.score} label={temp.label} /></div>
        <div className="flex-[3] flex gap-2.5">
          <DriverCard type="supply" title={ent && ent.value < 3.5 ? "利率探底" : "利率走低"}
            value={ent ? String(ent.value) : "--"} unit="%" delta={ent ? fmtDelta(ent.delta, ent.prev_period) : ""} note={ent ? `企业贷款利率 · ${ent.period}` : ""} />
          <DriverCard type="demand" title={demand && demand.value < 60 ? "需求疲弱" : "需求回暖"}
            value={demand ? String(demand.value) : "--"} delta={demand ? fmtDelta(demand.delta, demand.prev_period) : ""} note={demand ? `贷款需求指数 · ${demand.period}` : ""} />
          <DriverCard type="risk" title={cons && cons.value < 50 ? "建筑链收缩" : "建筑链企稳"}
            value={cons ? String(cons.value) : "--"} delta={cons ? fmtDelta(cons.delta, cons.prev_period) : ""} note={cons ? `建筑业PMI · ${cons.period}` : ""} />
        </div>
      </div>

      {/* ── Section 1: Supply ── */}
      <div>
        <SectionHeader num={1} title="供给端：资金宽松，利率探底，渠道收窄" consequence="银行卷利率抢客，小贷出清提速，fintech 定价空间被压缩" />
        <div className="flex gap-3 mb-3">
          {ent && <MetricCard accent="blue" label="企业贷款利率" value={String(ent.value)} unit="%" detail={`${ent.period} · ${fmtDelta(ent.delta, ent.prev_period)}`} />}
          {house && <MetricCard accent="blue" label="住房贷款利率" value={String(house.value)} unit="%" detail={`${house.period} · ${fmtDelta(house.delta, house.prev_period)}`} />}
          {shiborIb && <MetricCard accent="teal" label="同业拆借利率" value={String(shiborIb.value)} unit="%" detail={`${shiborIb.period} · 银行资金成本 · ${fmtDelta(shiborIb.delta, shiborIb.prev_period)}`} />}
          {mlInstV !== null && <MetricCard accent="red" label="小贷机构数量" value={mlInstV.toLocaleString()} unit="家" detail={mlInstP} />}
        </div>
        <div className="flex gap-3 mb-3">
          {m2 && <MetricCard accent="teal" label="M2 同比增速" value={String(m2.value)} unit="%" detail={`${m2.period} · ${fmtDelta(m2.delta, m2.prev_period)}`} />}
          {m1 && <MetricCard accent="teal" label="M1 同比增速" value={String(m1.value)} unit="%" detail={`${m1.period} · ${fmtDelta(m1.delta, m1.prev_period)} · 新口径(含个人活期)`} />}
          {mpf && <MetricCard accent="teal" label="货币政策感受" value={String(mpf.value)} detail={`${mpf.period} · ${fmtDelta(mpf.delta, mpf.prev_period)}`} />}
          {profit && <MetricCard accent="teal" label="银行盈利指数" value={String(profit.value)} detail={`${profit.period} · ${fmtDelta(profit.delta, profit.prev_period)}`} />}
        </div>
        {rateLabels.length >= 2 && (
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="paper-panel border-subtle p-4">
              <div className="text-2xs font-medium text-ink-tertiary mb-2">贷款利率 vs 资金成本（货币政策执行报告+金融统计月报）</div>
              {(() => {
                // Build union of all rate series periods, clipped to enterprise_rate range
                const entPeriods = mapToLabels(series?.enterprise_rate || []);
                const minP = entPeriods[0] || "2015-01";
                const maxP = entPeriods[entPeriods.length - 1] || "2026-12";
                const allPeriods = new Set<string>();
                for (const s of [series?.enterprise_rate, series?.housing_rate, series?.shibor_ib]) {
                  if (s) for (const d of s) if (d.p >= minP && d.p <= maxP) allPeriods.add(d.p);
                }
                const labels = [...allPeriods].sort();
                return (
                  <Line data={{ labels, datasets: [
                    { label: "企业贷款利率", data: alignToLabels(labels, series?.enterprise_rate || []), borderColor: "#3B82F6", borderWidth: 2, pointRadius: 2, tension: 0.2, fill: false, spanGaps: true },
                    { label: "住房贷款利率", data: alignToLabels(labels, series?.housing_rate || []), borderColor: "#0D7C66", borderWidth: 2, pointRadius: 2, tension: 0.2, fill: false, spanGaps: true },
                    { label: "同业拆借利率", data: alignToLabels(labels, series?.shibor_ib || []), borderColor: "#C9A96E", borderWidth: 1.5, pointRadius: 1, borderDash: [4, 3], tension: 0.3, fill: false, spanGaps: true },
                  ] }} options={lineOpts(2, undefined, undefined, (v) => Number(v) + "%")} />
                );
              })()}
            </div>
            {mlLabels.length >= 2 && (
              <div className="paper-panel border-subtle p-4">
                <div className="text-2xs font-medium text-ink-tertiary mb-2">小额贷款 · 机构数量 vs 贷款余额（季度 · 金融监管总局）</div>
                <Line data={{ labels: mlLabels, datasets: [
                  { label: "机构数量", data: mapToValues(mlInstArr), borderColor: "#C4362C", borderWidth: 2, pointRadius: 2, tension: 0.3, fill: false, yAxisID: "y" },
                  { label: "贷款余额(亿)", data: alignToLabels(mlLabels, mlBalArr), borderColor: "#6B6158", borderWidth: 2, pointRadius: 2, tension: 0.3, fill: false, yAxisID: "y1", spanGaps: true },
                ] }} options={(() => {
                  const c = chartColors();
                  const tick = { color: c.axis, font: { size: 10 } };
                  return {
                    responsive: true, maintainAspectRatio: true, aspectRatio: 2,
                    interaction: { mode: "index" as const, intersect: false },
                    plugins: { legend: makeChartLegend(), tooltip: makeChartTooltip() },
                    scales: {
                      x: { grid: { color: c.grid }, ticks: { ...tick, maxRotation: 45, autoSkip: true, maxTicksLimit: 10 } },
                      y: { type: "linear" as const, position: "left" as const, grid: { color: c.grid }, ticks: { ...tick, callback: (v: number | string) => (Number(v) / 1000).toFixed(1) + "k" }, title: { display: true, text: "机构数", font: { size: 10 }, color: c.axis } },
                      y1: { type: "linear" as const, position: "right" as const, grid: { display: false }, ticks: { ...tick, callback: (v: number | string) => (Number(v) / 1000).toFixed(1) + "k" }, title: { display: true, text: "余额(亿)", font: { size: 10 }, color: c.axis } },
                    },
                  };
                })()} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Section 2: Demand — credit demand + borrower willingness ── */}
      <div>
        <SectionHeader num={2} title="需求端：消费贷萎缩，借贷意愿低迷" consequence="短期消费贷增速转负=居民不敢借，就业+收入预期持续走弱" />
        <div className="flex gap-3 mb-3">
          {hhShortConsYoy && <MetricCard accent="red" label="短期消费贷增速" value={String(hhShortConsYoy.value)} unit="%" detail={`${hhShortConsYoy.period} · 信贷收支表`} />}
          {hhShortBizYoy && <MetricCard accent="amber" label="短期经营贷增速" value={String(hhShortBizYoy.value)} unit="%" detail={`${hhShortBizYoy.period} · 信贷收支表`} />}
          {demand && <MetricCard accent="amber" label="贷款需求指数" value={String(demand.value)} detail={`${demand.period} · ${fmtDelta(demand.delta, demand.prev_period)}`} />}
          {emp && <MetricCard accent="red" label="就业感受指数" value={String(emp.value)} detail={`${emp.period} · ${fmtDelta(emp.delta, emp.prev_period)}`} />}
        </div>
        <div className="flex gap-3 mb-3">
          {lat(latest, "income_feel") && <MetricCard accent="amber" label="收入感受指数" value={String(lat(latest, "income_feel")!.value)} detail={`${lat(latest, "income_feel")!.period} · ${fmtDelta(lat(latest, "income_feel")!.delta, lat(latest, "income_feel")!.prev_period)}`} />}
          {lat(latest, "consumption") && <MetricCard accent="amber" label="消费意愿指数" value={String(lat(latest, "consumption")!.value)} detail={`${lat(latest, "consumption")!.period} · ${fmtDelta(lat(latest, "consumption")!.delta, lat(latest, "consumption")!.prev_period)}`} />}
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="paper-panel border-subtle p-4">
            <div className="text-2xs font-medium text-ink-tertiary mb-2">住户贷款结构增速（月度 · 信贷收支表 · 余额同比）</div>
            <Line data={{ labels: mapToLabels(series?.hh_short_consumer_yoy || []), datasets: [
              { label: "短期消费贷", data: mapToValues(series?.hh_short_consumer_yoy || []), borderColor: "#C4362C", borderWidth: 2, pointRadius: 2, tension: 0.3, fill: false },
              { label: "短期经营贷", data: alignToLabels(mapToLabels(series?.hh_short_consumer_yoy || []), series?.hh_short_biz_yoy || []), borderColor: "#B8860B", borderWidth: 2, pointRadius: 2, tension: 0.3, fill: false, spanGaps: true },
              { label: "中长期消费贷(房贷)", data: alignToLabels(mapToLabels(series?.hh_short_consumer_yoy || []), series?.hh_long_consumer_yoy || []), borderColor: "#3B82F6", borderWidth: 1.5, pointRadius: 2, borderDash: [4, 3], tension: 0.3, fill: false, spanGaps: true },
              { label: "中长期经营贷", data: alignToLabels(mapToLabels(series?.hh_short_consumer_yoy || []), series?.hh_long_biz_yoy || []), borderColor: "#0D9488", borderWidth: 1.5, pointRadius: 2, borderDash: [4, 3], tension: 0.3, fill: false, spanGaps: true },
            ] }} options={lineOpts(2, undefined, undefined, (v) => Number(v) + "%")} />
            </div>
          {demandLabels.length >= 2 && (
            <div className="paper-panel border-subtle p-4">
              <div className="text-2xs font-medium text-ink-tertiary mb-2">需求侧问卷指数（季度 · 银行家+储户问卷）</div>
              <Line data={{ labels: demandLabels, datasets: [
                { label: "贷款需求指数", data: mapToValues(series?.loan_demand || []), borderColor: "#B8860B", borderWidth: 2, pointRadius: 2, tension: 0.3, fill: false },
                { label: "就业感受指数", data: alignToLabels(demandLabels, series?.employment || []), borderColor: "#C4362C", borderWidth: 1.5, pointRadius: 2, borderDash: [4, 3], tension: 0.3, fill: false, spanGaps: true },
              ] }} options={lineOpts(2, undefined, undefined)} />
            </div>
          )}
        </div>
      </div>

      {/* ── Section 3: Real Economy Transmission ── */}
      <div>
        <SectionHeader num={3} title="实体传导：行业景气走弱" consequence="信贷资产质量承压，抵押物估值下移" />
        <div className="flex gap-3 mb-3">
          {cons && <MetricCard accent="red" label="建筑业 PMI" value={String(cons.value)} detail={`${cons.period} · ${fmtDelta(cons.delta, cons.prev_period)}`} />}
          {order && <MetricCard accent="red" label="服务业新订单" value={String(order.value)} detail={`${order.period} · ${fmtDelta(order.delta, order.prev_period)}`} />}
          {nfg && <MetricCard accent="teal" label="非制造业 PMI" value={String(nfg.value)} detail={`${nfg.period} · ${fmtDelta(nfg.delta, nfg.prev_period)}`} />}
          {ride && <MetricCard accent="green" label="网约车月订单" value={String(ride.value)} unit="亿" detail={`${ride.period} · ${fmtDelta(ride.delta, ride.prev_period)}`} />}
        </div>
        {pmiLabels.length >= 2 && (
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="paper-panel border-subtle p-4">
              <div className="text-2xs font-medium text-ink-tertiary mb-2">PMI 矩阵（月度 · 国家统计局 · 50=荣枯线）</div>
              <Line data={{ labels: pmiLabels, datasets: [
                { label: "非制造业", data: mapToValues(industry?.pmi_nfg || []), borderColor: "#6B6158", borderWidth: 2, pointRadius: 2, tension: 0.3, fill: false },
                { label: "服务业", data: mapToValues(industry?.pmi_svc || []), borderColor: "#3B82F6", borderWidth: 1.5, pointRadius: 2, tension: 0.3, fill: false },
                { label: "建筑业", data: mapToValues(industry?.pmi_cons || []), borderColor: "#C4362C", borderWidth: 2, pointRadius: 2, tension: 0.3, fill: false },
                { label: "新订单", data: mapToValues(industry?.pmi_order || []), borderColor: "#B8860B", borderWidth: 1.5, pointRadius: 2, borderDash: [4, 3], tension: 0.3, fill: false },
              ] }} options={lineOpts(2, undefined, undefined)} />
            </div>
            {reInvData.length >= 2 && (
              <div className="paper-panel border-subtle p-4">
                <div className="text-2xs font-medium text-ink-tertiary mb-2">房地产开发投资完成额（月度 · 国家统计局 · 亿元）</div>
                <Bar data={{ labels: mapToLabels(reInvData), datasets: [{
                  data: mapToValues(reInvData),
                  backgroundColor: reInvData.map((d) => d.v < 6000 ? "rgba(196,54,44,0.6)" : "rgba(107,97,88,0.4)"),
                  borderRadius: 3, maxBarThickness: 28,
                }] }} options={(() => {
                  const c = chartColors();
                  return {
                    responsive: true, maintainAspectRatio: true, aspectRatio: 2,
                    plugins: { legend: { display: false }, tooltip: { ...makeChartTooltip(), callbacks: { label: (ctx) => (ctx.parsed.y ?? 0).toLocaleString() + "亿元" } } },
                    scales: { x: { grid: { display: false }, ticks: { color: c.axis, font: { size: 10 } } }, y: { grid: { color: c.grid }, ticks: { color: c.axis, font: { size: 10 }, callback: (v: number | string) => (Number(v) / 1000).toFixed(0) + "k" }, suggestedMin: 3000 } },
                  };
                })()} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Section 4: Post-loan Risk ── */}
      <div>
        <SectionHeader num={4} title="贷后风险：失业率走高、偿债能力走弱" consequence="失业率↑ → 可支配收入↓ → 循环信贷逾期上升、不良出清加速" />
        {/* Row 1: Leading indicators — macro employment (objective) */}
        <div className="flex gap-3 mb-3">
          {urateNation && <MetricCard accent="amber" label="城镇调查失业率" value={String(urateNation.value)} unit="%" detail={`${urateNation.period} · 全国 · ${fmtDelta(urateNation.delta, urateNation.prev_period)}`} />}
          {urate31Cities && <MetricCard accent="amber" label="31大城市失业率" value={String(urate31Cities.value)} unit="%" detail={`${urate31Cities.period} · 消金核心客群 · ${fmtDelta(urate31Cities.delta, urate31Cities.prev_period)}`} />}
          {urateYouth && <MetricCard accent="red" label="16-24岁青年失业率" value={String(urateYouth.value)} unit="%" detail={`${urateYouth.period} · 新入行人口(不含在校生) · ${fmtDelta(urateYouth.delta, urateYouth.prev_period)}`} />}
          {emp && <MetricCard accent="red" label="就业感受指数" value={String(emp.value)} detail={`${emp.period} · 储户问卷 · ${fmtDelta(emp.delta, emp.prev_period)}`} />}
        </div>
        {/* Row 2: Lagging indicators — from DB when available, else hide */}
        {lat(latest, "consumption") && (
          <div className="flex gap-3 mb-3">
            {lat(latest, "consumption") && <MetricCard accent="amber" label="消费意愿指数" value={String(lat(latest, "consumption")!.value)} detail={`${lat(latest, "consumption")!.period} · 储户问卷 · ${fmtDelta(lat(latest, "consumption")!.delta, lat(latest, "consumption")!.prev_period)}`} />}
            {lat(latest, "income_feel") && <MetricCard accent="amber" label="收入感受指数" value={String(lat(latest, "income_feel")!.value)} detail={`${lat(latest, "income_feel")!.period} · 储户问卷 · ${fmtDelta(lat(latest, "income_feel")!.delta, lat(latest, "income_feel")!.prev_period)}`} />}
            {hhShortConsYoy && <MetricCard accent="red" label="短期消费贷增速" value={String(hhShortConsYoy.value)} unit="%" detail={`${hhShortConsYoy.period} · 信贷收支表`} />}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Left: Unemployment rate — objective leading indicator */}
          {(industry?.urate_nation?.length || 0) >= 2 && (() => {
            const urateLabels = mapToLabels(industry?.urate_nation || []);
            return (
              <div className="paper-panel border-subtle p-4">
                <div className="text-2xs font-medium text-ink-tertiary mb-2">失业率矩阵（月度 · 国家统计局 · 消费信贷领先指标）</div>
                <Line data={{ labels: urateLabels, datasets: [
                  { label: "全国城镇调查", data: mapToValues(industry?.urate_nation || []), borderColor: "#B8860B", borderWidth: 2, pointRadius: 2, tension: 0.3, fill: false },
                  { label: "31大城市", data: alignToLabels(urateLabels, industry?.urate_31cities || []), borderColor: "#3B82F6", borderWidth: 1.5, pointRadius: 2, tension: 0.3, fill: false, spanGaps: true },
                  { label: "16-24岁青年", data: alignToLabels(urateLabels, industry?.urate_youth || []), borderColor: "#C4362C", borderWidth: 2, pointRadius: 2, borderDash: [4, 3], tension: 0.3, fill: false, spanGaps: true },
                ] }} options={lineOpts(2, undefined, undefined, (v) => Number(v) + "%")} />
              </div>
            );
          })()}
          {/* Right: Employment feeling survey */}
          {empLabels.length >= 2 && (
            <div className="paper-panel border-subtle p-4">
              <div className="text-2xs font-medium text-ink-tertiary mb-2">就业感受指数（季度 · 城镇储户问卷调查）</div>
              <Line data={{ labels: empLabels, datasets: [
                { label: "就业感受指数", data: mapToValues(series?.employment || []), borderColor: "#C4362C", borderWidth: 2, pointRadius: 2, tension: 0.3, fill: { target: "origin", above: "rgba(196,54,44,0.06)" } },
              ] }} options={lineOpts(2, undefined, undefined)} />
            </div>
          )}
        </div>
        {/* Transmission flow — full width for visual punch */}
        <div className="paper-panel border-subtle p-4">
          <div className="text-2xs font-medium text-ink-tertiary mb-3">传导链：宏观就业 → 居民偿债 → 信贷质量</div>
          <div className="flex items-center gap-2 flex-wrap justify-center text-xs">
            {[
              { label: "失业率走高", val: urateYouth ? `青年 ${urateYouth.value}%` : (urateNation ? `全国 ${urateNation.value}%` : "--"), cls: "bg-negative/5 border-negative/15" },
              { label: "收入预期走弱", val: lat(latest, "income_feel") ? `收入感受 ${lat(latest, "income_feel")!.value}` : (emp ? `就业感受 ${emp.value}` : "--"), cls: "bg-negative/5 border-negative/15" },
              { label: "消费贷收缩", val: hhShortConsYoy ? `增速 ${hhShortConsYoy.value}%` : "--", cls: "bg-warning/5 border-warning/15" },
              { label: "消费意愿低迷", val: lat(latest, "consumption") ? `意愿 ${lat(latest, "consumption")!.value}%` : "--", cls: "bg-warning/5 border-warning/15" },
              { label: "催收承压", val: lat(latest, "loan_demand") ? `需求 ${lat(latest, "loan_demand")!.value}` : "--", cls: "bg-surface-sunken border-border" },
            ].map((n, i) => (
              <span key={i} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-ink-faint">→</span>}
                <span className={`px-2.5 py-1.5 rounded border text-center ${n.cls}`}>
                  <div className="text-2xs font-semibold text-ink-secondary">{n.label}</div>
                  <div className="text-xs font-bold text-ink tabular-nums">{n.val}</div>
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── So What ── */}
      <div className="paper-panel border-subtle card-accent-left p-6">
        <h2 className="text-base font-bold text-ink mb-2.5">So What：对信贷行业从业者意味着什么</h2>
        <p className="text-sm text-ink-secondary leading-relaxed mb-2">
          四条传导链共同指向一个结论——信贷行业正处于<span className="font-semibold text-ink">「量缩价跌质降」</span>的结构性下行期，
          但这不是均匀下行，而是<span className="font-semibold text-ink">分化加剧</span>：
        </p>
        <ul className="space-y-1 text-xs text-ink-secondary leading-relaxed pl-4 list-disc">
          <li><strong className="text-ink">供给端：</strong>企业贷款利率{ent ? ` ${ent.value}%` : "探底"}，同业拆借利率{shiborIb ? ` ${shiborIb.value}%` : "低位"} = 资金成本便宜但银行净息差极度压缩{mlInstV ? `；小贷机构 ${mlInstV.toLocaleString()} 家` : ""}</li>
          <li><strong className="text-ink">信贷规模：</strong>{m2 ? `M2 增速 ${m2.value}%` : "M2 高增"}但{loan ? `贷款增速 ${loan.value}%` : "贷款增速走低"} = 钱淤在金融体系{hhShortConsYoy ? `；短期消费贷增速 ${hhShortConsYoy.value}%` : ""} = 消费信贷需求萎缩信号</li>
          <li><strong className="text-ink">需求端：</strong>{emp ? `就业感受指数 ${emp.value}` : "就业感受低迷"}{lat(latest, "consumption") ? `，消费意愿 ${lat(latest, "consumption")!.value}%` : ""} = 居民不敢借、不愿花</li>
          <li><strong className="text-ink">贷后风险：</strong>{urateYouth ? `16-24 岁青年失业率 ${urateYouth.value}%` : "青年失业率高位"}{urate31Cities ? `、31 大城市失业率 ${urate31Cities.value}%` : ""} = 消费信贷核心客群可支配收入承压</li>
          <li><strong className="text-ink">关注拐点：</strong>短期消费贷增速转正 = 消费复苏信号；青年失业率回落 = 偿债能力企稳；M2-贷款增速剪刀差收窄 = 宽信用传导打通</li>
        </ul>
      </div>

      {/* Footer */}
      <div className="text-right text-2xs text-ink-faint">
        Credit Intelligence · 数据来源：中国人民银行、东方财富、交通运输部、金融监管总局
        {data.generated_at && <> · 更新于 {new Date(data.generated_at).toLocaleDateString("zh-CN")}</>}
      </div>
    </div>
  );
}
